import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const INITIAL_FORM = {
  name: "",
  designation: "",
  image: "",
  flatNumber: "",
  blockNumber: "",
  mobileNumber: "",
  email: "",
  status: "active",
};

const getTenantId = (t) => t?._id || t?.id || t?.userid || t?.email;

const getDesignationId = (d) => d?.designationId || d?._id || d?.id || "";
const getDesignationName = (d) =>
  String(d?.designationName || d?.name || "").trim();

const pickDesignationList = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.designations)) return data.designations;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  return [];
};

const mapTenantToFormFields = (tenant) => ({
  email: String(tenant?.email || "").trim(),
  mobileNumber: String(
    tenant?.phone || tenant?.mobileNumber || tenant?.mobile || "",
  ).trim(),
  blockNumber: String(tenant?.blockNumber || tenant?.block || "").trim(),
  flatNumber: String(tenant?.flatNumber || "").trim(),
  name: String(tenant?.name || "").trim(),
  image: String(
    tenant?.profileImage || tenant?.image || tenant?.avatar || "",
  ).trim(),
});

/** Normalize presign response shapes to { uploadUrl, key, contentType }. */
function unwrapPresignPayload(d) {
  if (!d || typeof d !== "object") return null;

  const nested =
    d.upload && typeof d.upload === "object"
      ? d.upload
      : d.data?.upload && typeof d.data.upload === "object"
        ? d.data.upload
        : null;

  if (nested?.uploadUrl && (nested?.key || nested?.s3Key)) {
    return {
      ...d,
      ...nested,
      key: nested.key || nested.s3Key,
    };
  }
  if (d.uploadUrl && (d.key || d.s3Key)) {
    return { ...d, key: d.key || d.s3Key };
  }
  if (d.data && typeof d.data === "object" && d.data.uploadUrl) {
    return {
      ...d,
      ...d.data,
      key: d.data.key || d.data.s3Key,
    };
  }
  return d;
}

function pickConfirmedImageUrl(data) {
  if (!data || typeof data !== "object") return "";
  return String(
    data.imageUrl ||
      data.image ||
      data.data?.imageUrl ||
      data.data?.image ||
      "",
  ).trim();
}

function pickConfirmedS3Key(data, fallback) {
  if (!data || typeof data !== "object") return fallback || "";
  return String(
    data.s3Key || data.key || data.data?.s3Key || fallback || "",
  ).trim();
}

const formatDisplayDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const CommunityCreate = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const userId = useMemo(() => localStorage.getItem("userid"), []);
  const emailWrapRef = useRef(null);
  const designationWrapRef = useRef(null);
  const searchReqIdRef = useRef(0);
  const designationSearchReqIdRef = useRef(0);
  const imageInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [emailSearch, setEmailSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchingTenants, setSearchingTenants] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [designationSearch, setDesignationSearch] = useState("");
  const [designationSuggestions, setDesignationSuggestions] = useState([]);
  const [searchingDesignations, setSearchingDesignations] = useState(false);
  const [showDesignationSuggestions, setShowDesignationSuggestions] =
    useState(false);
  const [dateOfOccupancy, setDateOfOccupancy] = useState("");

  const clearLocalPreview = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  const onChange = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setForm((f) => ({ ...f, [key]: value }));
  };

  const applyTenant = useCallback(
    (tenant) => {
      if (!tenant) return;
      const mapped = mapTenantToFormFields(tenant);
      clearLocalPreview();
      setImageFile(null);
      if (imageInputRef.current) imageInputRef.current.value = "";
      setForm((f) => ({
        ...f,
        email: mapped.email || f.email,
        mobileNumber: mapped.mobileNumber || f.mobileNumber,
        blockNumber: mapped.blockNumber || f.blockNumber,
        flatNumber: mapped.flatNumber || f.flatNumber,
        name: f.name.trim() ? f.name : mapped.name || f.name,
        image: mapped.image || "",
      }));
      setImagePreview(mapped.image || "");
      setDateOfOccupancy(tenant?.dateOfOccupancy || "");
      setEmailSearch(mapped.email || "");
      setSuggestions([]);
      setShowSuggestions(false);
    },
    [clearLocalPreview],
  );

  const searchTenantsByEmail = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (!API_BASE_URL || !userId || q.length < 3) {
        setSuggestions([]);
        setSearchingTenants(false);
        return;
      }

      const reqId = ++searchReqIdRef.current;
      setSearchingTenants(true);
      try {
        const response = await axios.post(`${API_BASE_URL}`, {
          action: "admin.getTenants",
          userId,
          page: 1,
          limit: 25,
          search: q,
        });
        const data = response?.data;
        if (reqId !== searchReqIdRef.current) return;

        if (data?.status === "success" && Array.isArray(data?.tenants)) {
          setSuggestions(data.tenants);
          setShowSuggestions(true);

          const exact = data.tenants.find(
            (t) =>
              String(t?.email || "")
                .trim()
                .toLowerCase() === q.toLowerCase(),
          );
          if (exact) {
            applyTenant(exact);
          }
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        if (reqId !== searchReqIdRef.current) return;
        setSuggestions([]);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Tenant search failed";
        toast.error(msg);
      } finally {
        if (reqId === searchReqIdRef.current) {
          setSearchingTenants(false);
        }
      }
    },
    [API_BASE_URL, userId, applyTenant],
  );

  const applyDesignation = useCallback((row) => {
    if (!row) return;
    const name = getDesignationName(row);
    if (!name) return;
    setForm((f) => ({ ...f, designation: name }));
    setDesignationSearch(name);
    setDesignationSuggestions([]);
    setShowDesignationSuggestions(false);
  }, []);

  const searchDesignations = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (!API_BASE_URL) {
        setDesignationSuggestions([]);
        setSearchingDesignations(false);
        return;
      }

      const reqId = ++designationSearchReqIdRef.current;
      setSearchingDesignations(true);
      try {
        const payload = {
          action: "designation.getAll",
          page: 1,
          limit: 50,
        };
        if (q) payload.search = q;
        if (userId) {
          payload.userId = userId;
          payload.userid = userId;
        }

        const response = await axios.post(`${API_BASE_URL}`, payload);
        const data = response?.data;
        if (reqId !== designationSearchReqIdRef.current) return;

        if (data?.status === "success" || data?.success === true) {
          const list = pickDesignationList(data);
          setDesignationSuggestions(Array.isArray(list) ? list : []);
          setShowDesignationSuggestions(true);

          const exact = (Array.isArray(list) ? list : []).find(
            (d) => getDesignationName(d).toLowerCase() === q.toLowerCase(),
          );
          if (exact && q) {
            applyDesignation(exact);
          }
        } else {
          setDesignationSuggestions([]);
        }
      } catch (error) {
        if (reqId !== designationSearchReqIdRef.current) return;
        setDesignationSuggestions([]);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Designation search failed";
        toast.error(msg);
      } finally {
        if (reqId === designationSearchReqIdRef.current) {
          setSearchingDesignations(false);
        }
      }
    },
    [API_BASE_URL, userId, applyDesignation],
  );

  useEffect(() => {
    const q = emailSearch.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchTenantsByEmail(q);
    }, 400);
    return () => clearTimeout(timer);
  }, [emailSearch, searchTenantsByEmail]);

  useEffect(() => {
    const q = designationSearch.trim();
    // Search once user types (e.g. "sec"); empty focus loads via button/focus handler
    if (!q) {
      return undefined;
    }
    const timer = setTimeout(() => {
      searchDesignations(q);
    }, 400);
    return () => clearTimeout(timer);
  }, [designationSearch, searchDesignations]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!emailWrapRef.current?.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (!designationWrapRef.current?.contains(e.target)) {
        setShowDesignationSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(
    () => () => {
      clearLocalPreview();
    },
    [clearLocalPreview],
  );

  const onEmailInputChange = (e) => {
    const value = e?.target?.value ?? "";
    setEmailSearch(value);
    setForm((f) => ({ ...f, email: value }));
    setShowSuggestions(true);
  };

  const onDesignationInputChange = (e) => {
    const value = e?.target?.value ?? "";
    setDesignationSearch(value);
    setForm((f) => ({ ...f, designation: value }));
    setShowDesignationSuggestions(true);
  };

  /**
   * 1) getProfileImageUploadUrl (email before create)
   * 2) PUT binary to uploadUrl
   * 3) confirmProfileImage → public image URL
   */
  const uploadProfileImage = useCallback(
    async (email, file) => {
      if (!API_BASE_URL) {
        throw new Error("Missing REACT_APP_API_URL in environment");
      }
      const memberEmail = String(email || "").trim();
      if (!memberEmail) {
        throw new Error("Enter email before uploading a profile image.");
      }
      if (!file) {
        throw new Error("Please choose an image file.");
      }

      const fileName = file.name || "photo.jpg";
      const fileContentType =
        file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";

      const presignRes = await axios.post(`${API_BASE_URL}`, {
        action: "community.getProfileImageUploadUrl",
        email: memberEmail,
        fileName,
        contentType: fileContentType,
      });
      const presignRaw = presignRes?.data;
      const okPresign =
        presignRaw?.status === "success" ||
        presignRaw?.success === true ||
        !!presignRaw?.uploadUrl ||
        !!presignRaw?.upload?.uploadUrl ||
        !!presignRaw?.data?.uploadUrl;

      if (!okPresign) {
        throw new Error(presignRaw?.message || "Could not get upload URL");
      }

      const presign = unwrapPresignPayload(presignRaw);
      const uploadUrl = presign?.uploadUrl;
      const s3KeyFromPresign = presign?.key || presign?.s3Key;
      if (!uploadUrl || !s3KeyFromPresign) {
        throw new Error("Invalid upload URL response (missing uploadUrl/key).");
      }

      const contentTypeForPut =
        presign.contentType || fileContentType || "image/jpeg";

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentTypeForPut },
        body: file,
      });

      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => "");
        throw new Error(
          `S3 upload failed (${putRes.status}). ${
            errText
              ? errText.slice(0, 200)
              : "Check bucket CORS and Content-Type."
          }`,
        );
      }

      const confirmRes = await axios.post(`${API_BASE_URL}`, {
        action: "community.confirmProfileImage",
        email: memberEmail,
        s3Key: s3KeyFromPresign,
        fileName,
        contentType: contentTypeForPut,
      });
      const confirmData = confirmRes?.data;
      const okConfirm =
        confirmData?.status === "success" || confirmData?.success === true;

      if (!okConfirm) {
        throw new Error(
          confirmData?.message || "Could not confirm profile image",
        );
      }

      const imageUrl = pickConfirmedImageUrl(confirmData);
      if (!imageUrl) {
        throw new Error("Confirm succeeded but no image URL was returned");
      }

      return {
        imageUrl,
        s3Key: pickConfirmedS3Key(confirmData, s3KeyFromPresign),
      };
    },
    [API_BASE_URL],
  );

  const processImageFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (jpg, png, webp, gif).");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("File is too large (max 8 MB).");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }

    const email = (form.email || emailSearch).trim();
    if (!email) {
      toast.error("Enter / select email before uploading an image.");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }

    clearLocalPreview();
    setImageFile(null);
    setImagePreview("");
    setForm((f) => ({ ...f, image: "" }));

    const localUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = localUrl;
    setImageFile(file);
    setImagePreview(localUrl);

    setUploadingImage(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const { imageUrl } = await uploadProfileImage(email, file);
      clearLocalPreview();
      setImagePreview(imageUrl);
      setForm((f) => ({ ...f, image: imageUrl }));
      toast.success("Profile image uploaded");
    } catch (error) {
      const msg = error?.message || "Image upload failed";
      toast.error(msg);
      clearLocalPreview();
      setImageFile(null);
      setImagePreview("");
      setForm((f) => ({ ...f, image: "" }));
      if (imageInputRef.current) imageInputRef.current.value = "";
    } finally {
      if (didIncrement) loaderDecrement();
      setUploadingImage(false);
    }
  };

  const onImageFileChange = (e) => {
    const file = e.target.files?.[0];
    processImageFile(file);
  };

  const onDropImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploadingImage || submitting) return;
    const file = e.dataTransfer?.files?.[0];
    processImageFile(file);
  };

  const clearImage = () => {
    clearLocalPreview();
    setImageFile(null);
    setImagePreview("");
    setForm((f) => ({ ...f, image: "" }));
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const resetForm = () => {
    clearImage();
    setForm(INITIAL_FORM);
    setEmailSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setDesignationSearch("");
    setDesignationSuggestions([]);
    setShowDesignationSuggestions(false);
    setDateOfOccupancy("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    const name = form.name.trim();
    const designation = form.designation.trim();
    let image = form.image.trim();
    const flatNumber = form.flatNumber.trim();
    const blockNumber = form.blockNumber.trim();
    const mobileNumber = form.mobileNumber.trim();
    const email = form.email.trim();
    const status = "active";

    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!designation) {
      toast.error("Designation is required");
      return;
    }
    if (!flatNumber) {
      toast.error("Flat number is required");
      return;
    }
    if (!blockNumber) {
      toast.error("Block number is required");
      return;
    }
    if (!mobileNumber) {
      toast.error("Mobile number is required");
      return;
    }
    if (!email) {
      toast.error("Email is required");
      return;
    }

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;

      // If a file was chosen but upload didn't finish, upload now before create
      if (imageFile && !image) {
        const uploaded = await uploadProfileImage(email, imageFile);
        image = uploaded.imageUrl;
        setForm((f) => ({ ...f, image }));
        setImagePreview(image);
      }

      const payload = {
        action: "community.create",
        userId,
        name,
        designation,
        image: image || "",
        flatNumber,
        blockNumber,
        mobileNumber,
        email,
        status,
      };

      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        toast.success(data?.message || "Community member created successfully");
        resetForm();
        navigate("/community");
      } else {
        toast.error(data?.message || "Failed to create community member");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  const initials =
    (form.name || "CM")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "CM";

  return (
    <div className="employee-form community-create">
      <div className="content-header">
        <div className="container-fluid community-create-header">
          <div>
            <nav className="community-create-breadcrumb">
              <NavLink to="/community">Executive Committee </NavLink>
              <span>/</span>
              <span>Add member</span>
            </nav>
            <h1 className="m-0 txt_lft">Add Executive Committee </h1>
            <p className="community-create-sub">
              Search email to auto-fill details, then add designation and photo.
            </p>
          </div>
          <NavLink to="/community" className="btn btn-secondary">
            <i className="fa-solid fa-arrow-left mr-1" /> Back
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="row community-create-layout">
              {/* Photo panel (right on desktop) */}
              <div className="col-12 col-lg-4 order-2 order-lg-2">
                <div className="community-create-photo-card">
                  <div
                    className="community-create-photo-glow"
                    aria-hidden="true"
                  />
                  <div
                    className={`community-create-dropzone${dragOver ? " is-dragover" : ""}${
                      uploadingImage ? " is-uploading" : ""
                    }${form.image ? " has-image" : ""}`}
                    onClick={() => {
                      if (!uploadingImage && !submitting)
                        imageInputRef.current?.click();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!uploadingImage && !submitting)
                          imageInputRef.current?.click();
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={onDropImage}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload profile image"
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      className="d-none"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={onImageFileChange}
                      disabled={uploadingImage || submitting}
                    />

                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="community-create-avatar-img"
                        onError={() => {
                          if (!form.image) setImagePreview("");
                        }}
                      />
                    ) : (
                      <div className="community-create-avatar-fallback">
                        {initials}
                      </div>
                    )}

                    <div className="community-create-drop-hint">
                      {uploadingImage ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />{" "}
                          Uploading…
                        </>
                      ) : form.image ? (
                        <>
                          <i className="fa-solid fa-check" /> Click to change
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-cloud-arrow-up" /> Drop or
                          click
                        </>
                      )}
                    </div>
                  </div>

                  <p className="community-create-photo-note">
                    Email first · JPG/PNG · max 8 MB
                  </p>

                  {form.image ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary btn-block"
                      onClick={clearImage}
                      disabled={uploadingImage || submitting}
                    >
                      <i className="fa-solid fa-trash-can mr-1" /> Remove photo
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Form sections (left on desktop) */}
              <div className="col-12 col-lg-8 order-1 order-lg-1">
                <div className="community-create-main-card">
                  <section className="community-create-section">
                    <div className="community-create-section-head">
                      <span className="section-icon">
                        <i className="fa-solid fa-magnifying-glass" />
                      </span>
                      <div>
                        <h2>Find tenant</h2>
                        <p>Search email first — other fields auto-fill</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <div
                          className="form-group"
                          ref={emailWrapRef}
                          style={{ position: "relative" }}
                        >
                          <label className="form-label">
                            Email <span className="required-star">*</span>
                          </label>
                          <div className="input-group community-create-email-group">
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Search tenant email..."
                              value={emailSearch}
                              onChange={onEmailInputChange}
                              onFocus={() => {
                                if (suggestions.length)
                                  setShowSuggestions(true);
                              }}
                              required
                            />
                            <div className="input-group-append">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={
                                  searchingTenants ||
                                  emailSearch.trim().length < 3
                                }
                                onClick={() =>
                                  searchTenantsByEmail(emailSearch)
                                }
                                title="Search tenants"
                              >
                                {searchingTenants ? (
                                  <i className="fa-solid fa-spinner fa-spin" />
                                ) : (
                                  <i className="fa-solid fa-magnifying-glass" />
                                )}
                              </button>
                            </div>
                          </div>

                          {showSuggestions && suggestions.length > 0 ? (
                            <div className="community-create-suggestions">
                              {suggestions.map((t) => (
                                <button
                                  key={getTenantId(t)}
                                  type="button"
                                  className="community-create-suggestion"
                                  onClick={() => applyTenant(t)}
                                >
                                  <div className="sug-name">
                                    {t?.name || "Tenant"}
                                  </div>
                                  <div className="sug-meta">
                                    {t?.email || "—"}
                                    {t?.phone ? ` · ${t.phone}` : ""}
                                    {t?.block || t?.blockNumber
                                      ? ` · Block ${t.block || t.blockNumber}`
                                      : ""}
                                    {t?.flatNumber
                                      ? ` · Flat ${t.flatNumber}`
                                      : ""}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : null}

                          {showSuggestions &&
                          !searchingTenants &&
                          emailSearch.trim().length >= 3 &&
                          suggestions.length === 0 ? (
                            <div className="community-create-suggestions empty">
                              No tenants found
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="community-create-section">
                    <div className="community-create-section-head">
                      <span className="section-icon">
                        <i className="fa-solid fa-user" />
                      </span>
                      <div>
                        <h2>Member details</h2>
                        <p>Name and role in the community</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            Name <span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter full name"
                            value={form.name}
                            onChange={onChange("name")}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div
                          className="form-group"
                          ref={designationWrapRef}
                          style={{ position: "relative" }}
                        >
                          <label className="form-label">
                            Position <span className="required-star">*</span>
                          </label>
                          <div className="input-group community-create-email-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search designation..."
                              value={designationSearch}
                              onChange={onDesignationInputChange}
                              onFocus={() => {
                                setShowDesignationSuggestions(true);
                                if (!designationSuggestions.length) {
                                  searchDesignations(designationSearch.trim());
                                }
                              }}
                              required
                              autoComplete="off"
                            />
                            <div className="input-group-append">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={searchingDesignations}
                                onClick={() =>
                                  searchDesignations(designationSearch.trim())
                                }
                                title="Search designations"
                              >
                                {searchingDesignations ? (
                                  <i className="fa-solid fa-spinner fa-spin" />
                                ) : (
                                  <i className="fa-solid fa-magnifying-glass" />
                                )}
                              </button>
                            </div>
                          </div>

                          {showDesignationSuggestions &&
                          designationSuggestions.length > 0 ? (
                            <div className="community-create-suggestions">
                              {designationSuggestions.map((d, idx) => {
                                const name = getDesignationName(d);
                                const id = getDesignationId(d) || `des-${idx}`;
                                return (
                                  <button
                                    key={id}
                                    type="button"
                                    className="community-create-suggestion"
                                    onClick={() => applyDesignation(d)}
                                  >
                                    <div className="sug-name">
                                      {name || "Designation"}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}

                          {showDesignationSuggestions &&
                          !searchingDesignations &&
                          designationSearch.trim() &&
                          designationSuggestions.length === 0 ? (
                            <div className="community-create-suggestions empty">
                              No designations found
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="community-create-section last">
                    <div className="community-create-section-head">
                      <span className="section-icon">
                        <i className="fa-solid fa-building" />
                      </span>
                      <div>
                        <h2>Contact &amp; residence</h2>
                        <p>Phone, block and flat</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label className="form-label">
                            Mobile Number{" "}
                            <span className="required-star">*</span>
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter mobile number"
                            value={form.mobileNumber}
                            onChange={onChange("mobileNumber")}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label className="form-label">
                            Block Number{" "}
                            <span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. A"
                            value={form.blockNumber}
                            onChange={onChange("blockNumber")}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label className="form-label">
                            Flat Number <span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. 101"
                            value={form.flatNumber}
                            onChange={onChange("flatNumber")}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="form-group">
                          <label className="form-label text-muted">
                            Date of Occupancy
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={formatDisplayDate(dateOfOccupancy)}
                            placeholder="Auto-filled from tenant"
                            readOnly
                            tabIndex={-1}
                            style={{
                              backgroundColor: "#f1f3f5",
                              color: "#868e96",
                              cursor: "not-allowed",
                              borderColor: "#e9ecef",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="community-create-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={resetForm}
                      disabled={submitting || uploadingImage}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary community-create-submit"
                      disabled={submitting || uploadingImage}
                    >
                      {submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-1" />{" "}
                          Creating…
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-user-plus mr-1" /> Create
                          Member
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommunityCreate;
